<template>
  <div class="dashboard">
    <div class="main-content">
      <navbar-dashboard name="Dashboard"></navbar-dashboard>

      <div class="ew-view">
        <page-header title="Dashboard" :subtitle="periodLabel">
          <div class="ew-period">
            <select class="custom-select" v-model="period" @change="onPeriodChange" aria-label="Periodo">
              <option v-for="p in periodOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
            </select>
          </div>
        </page-header>

        <!-- Indicadores -->
        <div class="ew-stats">
          <stat-card v-for="kpi in kpiTotals" :key="kpi.key" :label="kpi.label" :icon="kpi.icon" :tone="kpi.tone"
            :loading="loadingState.kpis" :value="kpi.value.toLocaleString('es-MX')">
            <div v-if="!loadingState.kpis && kpi.delta !== null" class="ew-delta"
              :class="kpi.delta > 0 ? 'is-up' : kpi.delta < 0 ? 'is-down' : ''">
              <i class="bi" :class="kpi.delta > 0 ? 'bi-arrow-up-right' : kpi.delta < 0 ? 'bi-arrow-down-right' : 'bi-dash'"></i>
              {{ kpi.delta > 0 ? '+' : '' }}{{ kpi.delta.toLocaleString('es-MX') }}%
              <span class="text-muted">vs. {{ comparisonLabel }}</span>
            </div>
            <div v-else-if="!loadingState.kpis" class="ew-delta text-muted">Sin base suficiente para comparar</div>
          </stat-card>
        </div>

        <!-- Filtros -->
        <div class="card ew-filters ew-dash-filters mb-3">
          <div class="form-row">
            <div class="col-12 col-lg-4" v-if="user_rol === 'superadmin'">
              <client-select id="clientSearch" label="Cliente" :value="filters.client"
                :selected-label="selectedClientLabel" :options="clientOptions" :loading="isLoadingClients"
                :loading-more="isLoadingMoreClients" @search="onClientSearch" @select="onClientSelect"
                @load-more="onClientLoadMore" />
            </div>
            <div class="col-6" :class="user_rol === 'superadmin' ? 'col-lg-4' : 'col-lg-6'">
              <searchable-select id="carrier" label="Paquetería" :value="filters.carrier" :options="carrierOptions"
                @input="onCarrierChange" placeholder="Todas" search-placeholder="Buscar paquetería..." />
            </div>
            <div class="col-6" :class="user_rol === 'superadmin' ? 'col-lg-4' : 'col-lg-6'">
              <searchable-select id="shipmentType" label="Tipo de envío" :value="filters.shipmentType"
                :options="shipmentTypeOptions" @input="onShipmentTypeChange" placeholder="Todos"
                search-placeholder="Buscar tipo..." />
            </div>
          </div>
        </div>

        <!-- Gráficas -->
        <div class="ew-charts">
          <div class="ew-section ew-chart">
            <h2 class="ew-section-title">Rutas principales</h2>
            <p class="ew-section-subtitle">Las 10 rutas (origen → destino) con más envíos en el periodo.</p>
            <div v-if="loadingState.cities" class="ew-skeleton ew-chart-skeleton"></div>
            <empty-state v-else-if="chartEmpty.cities" compact icon="bi bi-bar-chart" title="Sin envíos en este periodo"></empty-state>
            <canvas id="barOriginsDest" v-show="!loadingState.cities && !chartEmpty.cities"></canvas>
          </div>

          <div class="ew-section ew-chart">
            <h2 class="ew-section-title">Destinos más frecuentes</h2>
            <p class="ew-section-subtitle">Las 5 ciudades destino con más envíos y su participación entre ellas.</p>
            <template v-if="loadingState.destinations">
              <span class="ew-skeleton mb-3" v-for="n in 4" :key="n" style="height: 44px;"></span>
            </template>
            <ol v-else-if="topDestinations.length" class="ew-rank">
              <li v-for="(dest, index) in topDestinations" :key="index">
                <span class="ew-rank-pos">{{ index + 1 }}</span>
                <div class="ew-rank-body">
                  <div class="d-flex justify-content-between">
                    <span class="ew-rank-name">{{ [dest.city, dest.state_short].filter(Boolean).join(', ') }}</span>
                    <span class="ew-rank-pct">{{ dest.percentage }}%</span>
                  </div>
                  <div class="ew-rank-bar"><span :style="{ width: dest.percentage + '%' }"></span></div>
                  <div class="ew-rank-meta">{{ dest.totalShipments.toLocaleString('es-MX') }} envíos</div>
                </div>
              </li>
            </ol>
            <empty-state v-else compact icon="bi bi-geo-alt" title="Sin destinos en este periodo"></empty-state>
          </div>

          <div class="ew-section ew-chart">
            <h2 class="ew-section-title">Envíos por paquetería</h2>
            <p class="ew-section-subtitle">Distribución de guías generadas.</p>
            <div v-if="loadingState.carriers" class="ew-skeleton ew-chart-skeleton"></div>
            <empty-state v-else-if="chartEmpty.carriers" compact icon="bi bi-pie-chart" title="Sin envíos en este periodo"></empty-state>
            <canvas id="pieCarriers" v-show="!loadingState.carriers && !chartEmpty.carriers"></canvas>
          </div>

          <div class="ew-section ew-chart">
            <h2 class="ew-section-title">Envíos por tipo de paquete</h2>
            <p class="ew-section-subtitle">Sobres, cajas y tarimas.</p>
            <div v-if="loadingState.productTypes" class="ew-skeleton ew-chart-skeleton"></div>
            <empty-state v-else-if="chartEmpty.productTypes" compact icon="bi bi-box-seam" title="Sin envíos en este periodo"></empty-state>
            <canvas id="barProductTypes" v-show="!loadingState.productTypes && !chartEmpty.productTypes"></canvas>
          </div>

          <div class="ew-section ew-chart" v-if="hasRole(['helpdesk', 'sales', 'client'])">
            <h2 class="ew-section-title">Recargas por mes</h2>
            <p class="ew-section-subtitle">Monto recargado al monedero.</p>
            <div v-if="loadingState.recargas" class="ew-skeleton ew-chart-skeleton"></div>
            <empty-state v-else-if="chartEmpty.recargas" compact icon="bi bi-wallet2" title="Sin recargas en este periodo"></empty-state>
            <canvas id="chartRecargasMes" v-show="!loadingState.recargas && !chartEmpty.recargas"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from './Header/Navbar'
import axios from 'axios';
import Chart from 'chart.js';
import commons from '../commons';
import { roleMixin } from '@/mixins/roleMixin';
import SearchableSelect from './SearchableSelect.vue';
import ClientSelect from './ClientSelect.vue';

const CHART_PALETTE = ['#031d7d', '#a80b5f', '#2563eb', '#15803d', '#d97706', '#6b7185', '#9aa0b1', '#c5c9d6'];

// Opciones comunes de barras (sintaxis de Chart.js 2.x)
const BAR_OPTIONS = {
  responsive: true,
  legend: { display: false },
  scales: {
    xAxes: [{
      gridLines: { display: false },
      ticks: { fontColor: '#6b7185', fontFamily: 'Inter, sans-serif', fontSize: 11 },
    }],
    yAxes: [{
      gridLines: { color: '#eceef5', zeroLineColor: '#dfe2ec', drawBorder: false },
      ticks: { beginAtZero: true, precision: 0, fontColor: '#9aa0b1', fontFamily: 'Inter, sans-serif', fontSize: 11 },
    }],
  },
};

const HBAR_OPTIONS = {
  responsive: true,
  legend: { display: false },
  scales: {
    xAxes: [{
      gridLines: { color: '#eceef5', zeroLineColor: '#dfe2ec', drawBorder: false },
      ticks: { beginAtZero: true, precision: 0, fontColor: '#9aa0b1', fontFamily: 'Inter, sans-serif', fontSize: 11 },
    }],
    yAxes: [{
      gridLines: { display: false },
      ticks: { fontColor: '#4a5064', fontFamily: 'Inter, sans-serif', fontSize: 11 },
    }],
  },
};

export default {
  mixins: [roleMixin],
  data() {
    return {
      filters: {
        fromDate: '',
        toDate: '',
        carrier: '',
        state: '',
        shipmentType: '',
        client: "",
      },
      clientSearchQuery: '',
      clientSearchTimeout: null,
      clients: [],
      carriers: [],
      shipmentTypesMap: {
        'document': 'Sobre',
        'box': 'Caja',
        'tarima': 'Tarima'
      },
      kpiTotals: [
        { key: 'totalGenerated', label: 'Guías generadas', value: 0, delta: null, tone: 'navy', icon: "bi bi-box-seam" },
        { key: 'totalInTransit', label: 'En tránsito', value: 0, delta: null, tone: 'warning', icon: "bi bi-truck" },
        { key: 'totalDelivered', label: 'Entregados', value: 0, delta: null, tone: 'success', icon: "bi bi-check2-circle" },
      ],
      period: 'month',
      periodOptions: [
        { value: 'month', label: 'Este mes' },
        { value: 'lastMonth', label: 'Mes anterior' },
        { value: '30d', label: 'Últimos 30 días' },
        { value: '90d', label: 'Últimos 90 días' },
      ],
      charts: {},
      topDestinations: [],
      user_rol: '',
      loadingState: {
        kpis: true,
        cities: true,
        destinations: true,
        carriers: true,
        productTypes: true,
        recargas: true,
      },
      //SELECT CON PAGINACION ROL USUARIO
      pagination: {
        page: 1,
        limit: 20, // Cantidad por carga
        loading: false,
        hasMore: true
      },
      currentPage: 1,
      totalPages: 1,
      isLoadingClients: false,
      isLoadingMoreClients: false,
      selectedClientLabel: 'Todos',
      reportsRequestId: 0,
      chartEmpty: {
        cities: false,
        carriers: false,
        productTypes: false,
        recargas: false,
      },
      showUserDropdown: false,
      selectedUserId: '',
      selectedUserDisplay: '',
    };
  },
  created() {
    this.user_rol = localStorage.getItem('ew-user-role');
  },
  mounted() {
    // BUG: en el primer login, 'ew-user-parentClient' aún no existe en
    // localStorage (Sidebar.vue lo escribe async, después de que este
    // componente ya montó), así que getItem regresa null real (no el
    // string "null" que espera fetchReports/el backend). Axios omite
    // params null, el backend recibe client=undefined y por seguridad
    // regresa todo en ceros. Normalizamos siempre a un string válido:
    // 'all' para staff interno, o el id del cliente (propio o del padre)
    // para el resto, nunca null.
    // El filtro de cliente/empresa/usuario (con selector) solo aplica a
    // superadmin, quien ve "Todos" por defecto y puede elegir. El resto de
    // roles (incluyendo helpdesk/sales) siempre queda acotado a su propio
    // alcance: el cliente padre si son un sub-usuario, o su propio id si
    // son ellos mismos el cliente de nivel superior.
    const isSuperadmin = this.user_rol === 'superadmin';
    const parentClient = localStorage.getItem('ew-user-parentClient');
    const hasValidParent = parentClient && parentClient !== 'null' && parentClient !== 'undefined';
    this.filters.client = isSuperadmin
      ? 'all'
      : (hasValidParent ? parentClient : localStorage.getItem('ew-user-id'));

    this.applyPeriod();

    //this.fetchTrackingAll();
    this.fetchReports();
    if (isSuperadmin) {
      this.fetchClients(1);
    }
    this.fetchCarrierFromShipment();
  },
  computed: {
    // Rango [desde, hasta] en fechas locales para el periodo elegido
    range() {
      return this.rangeFor(this.period);
    },
    // Periodo inmediato anterior de la misma duración, para comparar KPIs
    previousRange() {
      const { from, to } = this.range;
      if (!from || !to) return null;
      if (this.period === 'month' || this.period === 'lastMonth') {
        return {
          from: new Date(from.getFullYear(), from.getMonth() - 1, 1),
          to: new Date(from.getFullYear(), from.getMonth(), 0),
        };
      }
      const days = Math.round((to - from) / 86400000) + 1;
      return {
        from: new Date(from.getFullYear(), from.getMonth(), from.getDate() - days),
        to: new Date(from.getFullYear(), from.getMonth(), from.getDate() - 1),
      };
    },
    periodLabel() {
      const { from, to } = this.range;
      if (!from || !to) return 'Selecciona un rango de fechas.';
      const fmt = (d, withYear) => d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: withYear ? 'numeric' : undefined });
      return `Resumen de envíos del ${fmt(from, from.getFullYear() !== to.getFullYear())} al ${fmt(to, true)}.`;
    },
    comparisonLabel() {
      if (this.period === 'month') return 'mes anterior';
      if (this.period === 'lastMonth') return 'el mes previo';
      return 'periodo anterior';
    },
    carrierOptions() {
      return [
        { value: 'all', label: 'Todas' },
        ...this.carriers.map((c) => ({ value: c, label: this.$options.filters.carrierName(c) })),
      ];
    },
    shipmentTypeOptions() {
      return [
        { value: 'all', label: 'Todos' },
        ...Object.entries(this.shipmentTypesMap).map(([key, label]) => ({ value: key, label })),
      ];
    },
    clientOptions() {
      return this.clients.map((c) => ({
        value: c._id,
        label: c.company && c.company.internalClientName
          ? c.company.internalClientName
          : `${c.firstName || ''} ${c.lastName || ''}`.trim(),
        badge: c.rol === 'user' ? 'Usuario' : null,
      }));
    },
  },
  methods: {
    rangeFor(period) {
      const today = new Date();
      const y = today.getFullYear();
      const m = today.getMonth();
      const d = today.getDate();
      switch (period) {
        case 'month': return { from: new Date(y, m, 1), to: new Date(y, m + 1, 0) };
        case 'lastMonth': return { from: new Date(y, m - 1, 1), to: new Date(y, m, 0) };
        case '30d': return { from: new Date(y, m, d - 29), to: today };
        case '90d': return { from: new Date(y, m, d - 89), to: today };
        default: return { from: null, to: null };
      }
    },
    // El backend hace new Date(fecha): mandamos el inicio y fin del día en la
    // zona horaria del usuario para no perder el último día del rango.
    toParams(range) {
      const start = new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate());
      const end = new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate(), 23, 59, 59, 999);
      return { fromDate: start.toISOString(), toDate: end.toISOString() };
    },
    applyPeriod() {
      const { from, to } = this.range;
      if (!from || !to) return false;
      Object.assign(this.filters, this.toParams(this.range));
      return true;
    },
    onPeriodChange() {
      if (this.applyPeriod()) this.fetchReports();
    },
    fetchCarrierFromShipment() {
      axios.get(`${commons.apiURL}/api/v1/carrier/getCarrierFromShipments`)
        .then(res => {
          this.carriers = res.data.map(item => item.cve);
        })
        .catch(err => console.error(err));
    },

    onClientSearch(query) {
      this.clientSearchQuery = query;
      clearTimeout(this.clientSearchTimeout);
      this.clientSearchTimeout = setTimeout(() => {
        this.fetchClients(1, query);
      }, 400);
    },
    onClientLoadMore() {
      if (this.currentPage < this.totalPages) {
        this.fetchClients(this.currentPage + 1, this.clientSearchQuery);
      }
    },
    onClientSelect({ value, label }) {
      this.filters.client = value;
      this.selectedClientLabel = label;
      this.fetchReports();
    },
    onCarrierChange(val) {
      this.filters.carrier = val;
      this.fetchReports();
    },
    onShipmentTypeChange(val) {
      this.filters.shipmentType = val;
      this.fetchReports();
    },
    async fetchClients(page = 1, search = '') {
      if (this.isLoadingClients || this.isLoadingMoreClients) return;
      if (page === 1) {
        this.isLoadingClients = true;
      } else {
        this.isLoadingMoreClients = true;
      }

      try {
        // rol=client,user: incluye tanto clientes (empresas) como usuarios
        // individuales, para poder filtrar estadísticas de una persona
        // específica dentro de un cliente, no solo del cliente completo.
        const res = await axios.get(`${commons.apiURL}/api/v1/users`, {
          params: { rol: 'client,user', page: page, limit: 10, filter: search }
        });

        const newUsers = res.data.docs || [];
        this.totalPages = res.data.pages;
        this.currentPage = page;

        if (page === 1) {
          this.clients = newUsers;
        } else {
          this.clients = [...this.clients, ...newUsers];
        }
      } catch (err) {
        console.error("Error al obtener usuarios:", err);
      } finally {
        this.isLoadingClients = false;
        this.isLoadingMoreClients = false;
      }
    },
    fetchReports() {
      if (this.filters.client == 'null') return;

      // Cada llamada a fetchReports() invalida las respuestas de la
      // anterior: si el usuario cambia de cliente rápido, una respuesta
      // vieja que llega tarde ya no puede pisar los datos del cliente
      // actualmente seleccionado (antes no había ninguna protección).
      const requestId = ++this.reportsRequestId;
      this.loadingState = {
        kpis: true,
        cities: true,
        destinations: true,
        carriers: true,
        productTypes: true,
        recargas: true,
      };

      this.fetchTotals(requestId);
      this.fetchShipmentsByCity(requestId);
      this.fetchTopDestinations(requestId);
      this.fetchCarrierData(requestId);
      this.fetchProductTypes(requestId);
      this.fetchPrepaidsPerMonth(requestId);
    },
    fetchTrackingAll() {
      axios.get(`${commons.apiURL}/api/v1/tracking/all`)
        .then(res => {
          console.log('Tracking data: Actualizado');
        })
        .catch(err => console.error(err));
    },
    fetchPrepaidsPerMonth(requestId) {
      axios.get(`${commons.apiURL}/api/v1/reports/prepaids-per-month`, { params: this.filters })
        .then(res => {
          if (requestId !== this.reportsRequestId) return; // respuesta obsoleta, se ignora
          const chartData = res.data.chartData;
          this.chartEmpty.recargas = !chartData.values || chartData.values.length === 0;
          this.renderBarChart('chartRecargasMes', chartData);
        })
        .catch(err => console.error(err))
        .finally(() => {
          if (requestId === this.reportsRequestId) this.loadingState.recargas = false;
        });
    },
    fetchTopDestinations(requestId) {
      axios.get(`${commons.apiURL}/api/v1/reports/top-destination`, { params: this.filters })
        .then(res => {
          if (requestId !== this.reportsRequestId) return;
          this.topDestinations = res.data;
        })
        .catch(err => console.error(err))
        .finally(() => {
          if (requestId === this.reportsRequestId) this.loadingState.destinations = false;
        });
    },
    fetchTotals(requestId) {
      const url = `${commons.apiURL}/api/v1/reports/shipments-report`;
      const prev = this.previousRange
        ? axios.get(url, { params: { ...this.filters, ...this.toParams(this.previousRange) } }).catch(() => null)
        : Promise.resolve(null);
      Promise.all([axios.get(url, { params: this.filters }), prev])
        .then(([res, prevRes]) => {
          if (requestId !== this.reportsRequestId) return;
          const data = res.data;
          const before = prevRes ? prevRes.data : null;

          this.kpiTotals.forEach((kpi) => {
            kpi.value = Number(data[kpi.key]) || 0;
            const old = before ? Number(before[kpi.key]) || 0 : 0;
            // Con una base muy chica el porcentaje no dice nada (ej. 3 → 900 = +29,900%)
            const delta = old >= 20 ? Math.round(((kpi.value - old) / old) * 100) : null;
            kpi.delta = delta !== null && Math.abs(delta) <= 500 ? delta : null;
          });
        })
        .catch(err => console.error(err))
        .finally(() => {
          if (requestId === this.reportsRequestId) this.loadingState.kpis = false;
        });
    },
    async fetchCarrierData(requestId) {
      try {
        axios
          .get(`${commons.apiURL}/api/v1/reports/total-by-carrier`, { params: this.filters })
          .then(res => {
            if (requestId !== this.reportsRequestId) return;
            const data = res.data;

            const labels = data.map((item) => item.carrier ? this.$options.filters.carrierName(item.carrier) : "Sin nombre");
            const values = data.map((item) => item.totalShipments);
            const colors = labels.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]);
            this.chartEmpty.carriers = values.length === 0;

            this.renderPieChart("pieCarriers", {
              labels,
              values,
              colors,
            });

          })
          .catch(err => console.error(err))
          .finally(() => {
            if (requestId === this.reportsRequestId) this.loadingState.carriers = false;
          });

      } catch (error) {
        console.error("Error al cargar datos de carriers:", error);
        if (requestId === this.reportsRequestId) this.loadingState.carriers = false;
      }
    },
    fetchShipmentsByCity(requestId) {
      axios
        .get(`${commons.apiURL}/api/v1/reports/shipments-city`, { params: this.filters })
        .then(res => {
          if (requestId !== this.reportsRequestId) return;
          const data = res.data;
          // Mapear a formato que renderBarChart espera
          // Solo las 10 rutas con más envíos: el resto no se distingue en la gráfica
          const pairs = (data.labels || []).map((label, i) => ({ label, value: (data.data || [])[i] || 0 }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
          const chartData = {
            labels: pairs.map(p => p.label),
            values: pairs.map(p => p.value),
            label: 'Envíos',
            horizontal: true
          };
          this.chartEmpty.cities = !data.data || data.data.length === 0;
          this.renderBarChart('barOriginsDest', chartData);
        })
        .catch(err => console.error(err))
        .finally(() => {
          if (requestId === this.reportsRequestId) this.loadingState.cities = false;
        });
    },
    fetchProductTypes(requestId) {
      axios
        .get(`${commons.apiURL}/api/v1/reports/shipments-by-type`, { params: this.filters })
        .then(res => {
          if (requestId !== this.reportsRequestId) return;
          const data = res.data;
          // Traducimos el tipo si deseas mostrarlo más legible
          const typeLabels = {
            document: 'Sobre',
            box: 'Caja',
            tarima: 'Tarima'
          };

          const labels = data.map(item => typeLabels[item.shipment_type] || item.shipment_type);
          const values = data.map(item => item.totalShipments);
          this.chartEmpty.productTypes = values.length === 0;

          this.renderBarChart('barProductTypes', {
            label: 'Cantidad de envíos',
            labels,
            values
          });
        })
        .catch(err => console.error(err))
        .finally(() => {
          if (requestId === this.reportsRequestId) this.loadingState.productTypes = false;
        });
    },
    renderBarChartPrepaids(id, chartData) {
      // Validación: si no existe el canvas, no hacemos nada
      const canvas = document.getElementById(id);
      if (!canvas) return;

      if (!this.charts) this.charts = {};
      if (this.charts[id]) this.charts[id].destroy();

      const ctx = document.getElementById(id).getContext('2d');
      this.charts[id] = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: chartData.labels,
          datasets: [{
            label: chartData.label,
            data: chartData.values,
            backgroundColor: '#2C2E78',
            borderRadius: 10,
            borderSkipped: false
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: true }
          },
          scales: {
            x: { title: { display: true, text: 'Mes' } },
            y: { title: { display: true, text: 'Monto de recargas ($)' }, beginAtZero: true }
          }
        }
      });
    },
    renderBarChart(id, chartData) {
      // Validación: si no existe el canvas, no hacemos nada
      const canvas = document.getElementById(id);
      if (!canvas) return;

      if (!this.charts) this.charts = {};
      if (this.charts[id]) this.charts[id].destroy();

      const ctx = document.getElementById(id).getContext('2d');
      this.charts[id] = new Chart(ctx, {
        type: chartData.horizontal ? 'horizontalBar' : 'bar',
        data: {
          labels: chartData.labels,
          datasets: [{
            label: chartData.label,
            data: chartData.values,
            backgroundColor: '#031d7d',
            hoverBackgroundColor: '#a80b5f',
            maxBarThickness: 36
          }]
        },
        options: chartData.horizontal ? HBAR_OPTIONS : BAR_OPTIONS
      });
    },
    renderPieChart(id, chartData) {
      // Validación: si no existe el canvas, no hacemos nada
      const canvas = document.getElementById(id);
      if (!canvas) return;

      if (!this.charts) this.charts = {};
      if (this.charts[id]) this.charts[id].destroy();

      const ctx = document.getElementById(id).getContext("2d");

      this.charts[id] = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: chartData.labels,
          datasets: [
            {
              data: chartData.values,
              backgroundColor: chartData.colors,
              borderColor: '#ffffff',
              borderWidth: 2
            },
          ],
        },
        options: {
          responsive: true,
          cutoutPercentage: 62,
          legend: { position: "right", labels: { boxWidth: 10, fontColor: '#4a5064', fontFamily: 'Inter, sans-serif', padding: 14 } },
          tooltips: {
            callbacks: {
              label: function (tooltipItem) {
                  const total = chartData.values.reduce((a, b) => a + b, 0);
                  const value = chartData.values[tooltipItem.dataIndex];
                  const percentage = ((value / total) * 100).toFixed(1);
                  return (
                    chartData.labels[tooltipItem.dataIndex] +
                    ": " +
                    value +
                    " envíos (" +
                    percentage +
                    "%)"
                  );
              },
            },
          },
        },
      });
    }
  },
  components: {
    'navbar-dashboard': Navbar,
    SearchableSelect,
    ClientSelect,
  }
}
</script>
<style scoped>
.ew-period {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ew-period .custom-select {
  width: auto;
  min-width: 170px;
}

.ew-period .form-control {
  width: 160px;
}

.ew-delta {
  margin-top: 6px;
  color: #6b7185;
  font-size: 0.75rem;
  font-weight: 600;
}

.ew-delta.is-up {
  color: #15803d;
}

.ew-delta.is-down {
  color: #d92d20;
}

.ew-delta .text-muted {
  font-weight: 400;
}

.ew-dash-filters {
  padding-bottom: 0;
}

.ew-charts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.ew-charts .ew-section {
  margin-bottom: 0;
}

.ew-chart-skeleton {
  height: 240px;
  border-radius: 8px;
}

.ew-rank {
  margin: 0;
  padding: 0;
  list-style: none;
}

.ew-rank li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
}

.ew-rank li + li {
  border-top: 1px solid #eceef5;
}

.ew-rank-pos {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #e8ebf5;
  color: #031d7d;
  font-size: 0.8rem;
  font-weight: 700;
}

.ew-rank-body {
  flex: 1 1 auto;
  min-width: 0;
}

.ew-rank-name {
  color: #1c2030;
  font-size: 0.875rem;
  font-weight: 600;
}

.ew-rank-pct {
  color: #031d7d;
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.ew-rank-bar {
  height: 6px;
  margin: 6px 0 4px;
  overflow: hidden;
  border-radius: 999px;
  background: #eceef5;
}

.ew-rank-bar span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #031d7d;
}

.ew-rank-meta {
  color: #6b7185;
  font-size: 0.75rem;
}

@media (max-width: 991.98px) {
  .ew-charts {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Etatistics.vue